<template>
  <div>
  <el-dialog
    :title="isEdit ? '编辑章节信息' : '新增章节信息'"
    :visible.sync="dialogVisible"
    width="800px"
    top="5%"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="handleClose"
    class="chapter-add-dialog"
  >
    <div class="dialog-body">
      <el-form
        ref="chapterForm"
        :model="form"
        :rules="rules"
        label-width="100px"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="章编号" prop="chapterNo">
              <el-input
                v-model.number="form.chapterNo"
                type="number"
                min="1"
                placeholder="请输入章编号"
                size="small"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="节编号" prop="sectionNo">
              <el-input
                v-model.number="form.sectionNo"
                type="number"
                min="1"
                placeholder="请输入节编号"
                size="small"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="章节名称" prop="title">
          <el-input v-model="form.title" placeholder="请输入章节名称" maxlength="100" show-word-limit size="small" />
        </el-form-item>
        <el-form-item label="课程视频" prop="videoUrl">
          <el-upload
            class="video-uploader"
            drag
            action=""
            :http-request="handleVideoUpload"
            :show-file-list="false"
            :before-upload="beforeVideoUpload"
            accept="video/mp4,video/ogg,video/3gp,video/webm"
          >
            <div v-if="videoUploading" class="upload-trigger">
              <i class="el-icon-loading"></i>
              <div class="upload-text">上传中...</div>
            </div>
            <div v-else-if="form.videoUrl" class="uploaded-video">
              <span class="video-name" :title="form.videoName">{{ form.videoName }}</span>
              <i class="el-icon-circle-close remove-icon" @click.stop="removeVideo"></i>
            </div>
            <div v-else class="upload-trigger">
              <i class="el-icon-upload"></i>
              <div class="upload-text">将文件拖到此处，或<em>点击上传</em></div>
            </div>
          </el-upload>
          <div class="form-tip">只能上传 mp4/ogg/3gp/webm 视频格式，且不超过 800M</div>
        </el-form-item>
        <el-form-item label="课件上传" prop="materialUrl">
          <el-upload
            ref="materialUploadRef"
            class="material-uploader"
            action=""
            multiple
            :auto-upload="false"
            :on-change="handleMaterialFileChange"
            :show-file-list="false"
            :before-upload="beforeMaterialUpload"
            accept=".ppt,.pptx,.doc,.docx,.pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
          >
            <el-button size="small" type="primary">点击上传</el-button>
            <div v-if="form.materials && form.materials.length > 0" class="uploaded-material-list">
              <div v-for="(item, index) in form.materials" :key="index" class="material-item">
                <i class="el-icon-success"></i>
                <span class="material-name">{{ item.name }}</span>
                <i class="el-icon-circle-close remove-icon" @click.stop="removeMaterial(index)"></i>
              </div>
            </div>
            <div v-if="materialUploading" class="upload-tip">上传中...</div>
          </el-upload>
          <div class="form-tip">只能上传 ppt / docx / pdf 文件，且不超过 200M</div>
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">取消</el-button>
      <el-button type="primary" size="small" :loading="submitting" @click="handleSubmit">确定</el-button>
    </div>
  </el-dialog>
  </div>
</template>

<script>
import { addChapter, updateChapter } from '@/api/video/chapter'
import { uploadCourseMaterial, uploadVideo } from '@/api/video/course'

export default {
  name: 'VideoChapterAdd',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    editData: {
      type: Object,
      default: null,
    },
    courseId: {
      type: Number,
      default: null,
    },
    nextChapterNo: {
      type: Number,
      default: 1,
    },
    nextSectionNo: {
      type: Number,
      default: 1,
    },
  },
  components: {
  },
  data() {
    return {
      submitting: false,
      materialUploading: false,
      videoUploading: false,
      form: this.getEmptyForm(),
      rules: {
        chapterNo: [
          { required: true, message: '请输入章编号', trigger: 'blur' },
          { type: 'number', min: 1, message: '章编号必须为大于 0 的数字', trigger: 'blur' },
        ],
        sectionNo: [
          { required: true, message: '请输入节编号', trigger: 'blur' },
          { type: 'number', min: 1, message: '节编号必须为大于 0 的数字', trigger: 'blur' },
        ],
        title: [
          { required: true, message: '请输入章节名称', trigger: 'blur' },
        ],
      },
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    isEdit() {
      return !!this.editData
    },
  },
  watch: {
    visible(val) {
      if (val) {
        if (this.editData) {
          const materials = []
          if (this.editData.materialUrl) {
            // 兼容旧格式：单文件
            materials.push({
              url: this.editData.materialUrl,
              name: this.editData.materialName || '课件',
            })
          }
          // 兼容新格式：多文件
          if (this.editData.materials && Array.isArray(this.editData.materials)) {
            this.editData.materials.forEach(m => {
              materials.push({ url: m.url || m.materialUrl, name: m.name || m.materialName || '课件' })
            })
          }
          this.form = {
            chapterNo: this.editData.chapterNo != null ? Number(this.editData.chapterNo) : '',
            sectionNo: this.editData.sectionNo != null ? Number(this.editData.sectionNo) : '',
            title: this.editData.title || '',
            videoUrl: this.editData.videoUrl || '',
            videoName: this.editData.videoName || '',
            materials,
          }
        } else {
          this.form = this.getEmptyForm()
          // 章编号默认 1
          this.form.chapterNo = Number(this.nextChapterNo) || 1
          // 节编号默认 = 父组件传入的 nextSectionNo（章节数 + 1）
          this.form.sectionNo = Number(this.nextSectionNo) || 1
        }
        this.$nextTick(() => {
          if (this.$refs.chapterForm) {
            this.$refs.chapterForm.clearValidate()
          }
        })
      }
    },
  },
  methods: {
    getEmptyForm() {
      return {
        chapterNo: '',
        sectionNo: '',
        title: '',
        videoUrl: '',
        videoName: '',
        videoId: '',
        videoObjectName: '',
        materials: [],
      }
    },
    beforeVideoUpload(file) {
      const allowedTypes = ['video/mp4', 'video/ogg', 'video/3gp', 'video/webm']
      if (file.type && !allowedTypes.includes(file.type)) {
        this.$message.error('仅支持 mp4、ogg、3gp、webm 视频格式')
        return false
      }
      if (file.size / 1024 / 1024 > 800) {
        this.$message.error('视频文件不能超过 800M')
        return false
      }
      return true
    },
    handleVideoUpload(option) {
      const file = option.file || option
      this.videoUploading = true
      uploadVideo(file).then(res => {
        console.log('[视频上传响应]', res)
        if (res && (res.code === 200 || res.success) && res.data) {
          const data = res.data
          const inner = data.data || {}
          const videoUrl = typeof data === 'string'
            ? data
            : data.accessUrl
              || data.url
              || data.videoUrl
              || data.fileUrl
              || data.path
              || inner.objectName
              || inner.url
          if (!videoUrl) {
            this.$message.error('上传成功，但未获取到视频地址')
            return
          }
          // videoObjectName 提交给后端，videoUrl 用于前端预览
          this.form.videoObjectName = inner.objectName || data.objectName || ''
          this.form.videoUrl = this.resolveVideoUrl(videoUrl || inner.objectName || data.objectName)
          this.form.videoName = data.originalName || file.name
          this.form.videoId = data.id || data.videoId || data.fileId || ''
          this.form.videoObjectName = (inner.objectName || data.objectName || '')
          this.$message.success('视频上传成功')
        } else {
          this.$message.error((res && (res.msg || res.message)) || '视频上传失败')
        }
      }).catch(e => {
        console.error('[视频上传异常]', e)
        this.$message.error('视频上传失败：' + (e && e.message || '请稍后重试'))
      }).finally(() => {
        this.videoUploading = false
      })
    },
    resolveVideoUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let path = url.startsWith('/') ? url : `/${url}`
      path = path.replace(/^\/home\/file_upload/, '/files')
      const base = (
        this.$store.state.IPAss.yl ||
        process.env.VUE_APP_PREVIEW_FILE_URL ||
        ''
      ).replace(/\/$/, '')
      return `${base}${path}`
    },
    removeVideo() {
      this.form.videoUrl = ''
      this.form.videoName = ''
    },
    beforeMaterialUpload(file) {
      const allowedExt = ['ppt', 'pptx', 'doc', 'docx', 'pdf']
      const ext = (file.name.split('.').pop() || '').toLowerCase()
      console.log('[课件上传校验]', { name: file.name, ext, size: file.size / 1024 / 1024 })
      if (!allowedExt.includes(ext)) {
        this.$message.error('仅支持 ppt、pptx、doc、docx、pdf 文件')
        return false
      }
      if (file.size / 1024 / 1024 > 200) {
        this.$message.error('课件文件不能超过 200M')
        return false
      }
      return true
    },
    handleMaterialFileChange(_file, fileList) {
      // 收集本次新选择的文件
      if (this._pendingMaterialTimer) {
        clearTimeout(this._pendingMaterialTimer)
      }
      this._pendingMaterialTimer = setTimeout(() => {
        // 一次拿全部待上传文件，el-upload 内部 fileList 中 status==='ready' 表示待上传
        const files = (fileList || [])
          .filter(f => f.status === 'ready' && f.raw)
          .map(f => f.raw)
        // 清空内部 fileList，避免重复提交
        this.$refs.materialUploadRef && this.$refs.materialUploadRef.clearFiles()
        if (files.length === 0) return
        this.batchUploadMaterials(files)
      }, 50)
    },
    batchUploadMaterials(files) {
      console.log('[课件批量上传开始]', files.map(f => f.name))
      this.materialUploading = true
      uploadCourseMaterial(files).then(res => {
        console.log('[课件批量上传响应]', res)
        if (res && (res.code === 200 || res.success) && res.data) {
          // 兼容多种返回：字符串、对象、对象数组
          const data = res.data
          const arr = Array.isArray(data) ? data : [data]
          arr.forEach((item, idx) => {
            const inner = item.data || {}
            // 提交给后端的 fileList.fileUrl 优先用上传返回的 fileUrl 字段
            const fileUrl = item.fileUrl
              || item.accessUrl
              || item.url
              || item.materialUrl
              || item.path
              || inner.objectName
              || item.objectName
              || ''
            if (!fileUrl) return
            this.form.materials.push({
              // 提交给后端 fileList.fileUrl 优先用 fileUrl
              fileUrl,
              accessUrl: item.accessUrl || fileUrl,
              previewUrl: this.resolveMaterialUrl(item.accessUrl || fileUrl),
              name: item.originalName || (files[idx] ? files[idx].name : '课件'),
              filename: item.originalName || (files[idx] ? files[idx].name : ''),
              objectName: inner.objectName || item.objectName || '',
              fileId: item.id || item.fileId || '',
            })
          })
          this.$message.success(`课件上传成功（${files.length} 个）`)
        } else {
          this.$message.error((res && (res.msg || res.message)) || '课件上传失败')
        }
      }).catch(e => {
        console.error('[课件批量上传异常]', e)
        this.$message.error('课件上传失败：' + (e && e.message || '请稍后重试'))
      }).finally(() => {
        this.materialUploading = false
      })
    },
    resolveMaterialUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url)) return url
      let path = url.startsWith('/') ? url : `/${url}`
      path = path.replace(/^\/home\/file_upload/, '/files')
      const base = (
        this.$store.state.IPAss.yl ||
        process.env.VUE_APP_PREVIEW_FILE_URL ||
        ''
      ).replace(/\/$/, '')
      return `${base}${path}`
    },
    removeMaterial(index) {
      this.form.materials.splice(index, 1)
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    handleSubmit() {
      this.$refs.chapterForm.validate((valid) => {
        if (!valid) {
          this.$message.error('请完善必填项')
          return
        }
        this.doSave()
      })
    },
    async doSave() {
      if (this.materialUploading || this.videoUploading) {
        this.$message.warning('文件正在上传，请稍候')
        return
      }
      this.submitting = true
      try {
        // 课件 fileList：fileName/fileUrl 都取上传接口返回
        const fileList = (this.form.materials || []).map(m => ({
          fileName: m.filename || m.name || '',
          fileUrl: m.fileUrl || m.accessUrl || m.url || '',
        }))
        // video 对象：videoUrl 取上传接口返回的 objectName
        const video = this.form.videoUrl
          ? {
              videoId: this.form.videoId || 0,
              videoName: this.form.videoName || '',
              videoUrl: this.form.videoObjectName || this.form.videoUrl || '',
            }
          : null

        const data = {
          courseId: this.courseId,
          chapterNo: Number(this.form.chapterNo) || 0,
          sectionNo: Number(this.form.sectionNo) || 0,
          sectionName: this.form.title,
          sortOrder: Number(this.form.chapterNo) || 1,
          fileList,
          video: video || { videoId: 0, videoName: '', videoUrl: '' },
        }
        let res
        if (this.isEdit) {
          data.id = this.editData.id
          res = await updateChapter(data)
        } else {
          res = await addChapter(data)
        }
        if (res && (res.code === 200 || res.success)) {
          this.$message.success(this.isEdit ? '修改成功' : '新增成功')
          this.$emit('update:visible', false)
          this.$emit('saved', { ...this.form })
        } else {
          this.$message.error((res && (res.msg || res.message)) || '保存失败')
        }
      } catch (e) {
        console.error('[章节保存异常]', e)
        this.$message.error('保存失败：' + (e && e.message || '请稍后重试'))
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
::v-deep .el-dialog__body {
  padding: 20px 25px;
}

.form-tip {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}

/* 视频上传 */
.video-uploader {
  width: 100%;

  ::v-deep .el-upload {
    width: 100%;
  }

  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 130px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.upload-trigger {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #909399;
  font-size: 13px;

  i {
    font-size: 32px;
    color: #c0c4cc;
    margin-bottom: 8px;
  }

  .upload-text {
    em {
      color: #409eff;
      font-style: normal;
    }
  }
}

.uploaded-video {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  color: #67c23a;

  .video-name {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .remove-icon {
    font-size: 16px;
    color: #f56c6c;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #c45656;
    }
  }
}

/* 课件上传 */
.material-uploader {
  display: flex;
  align-items: center;
  gap: 12px;
}

.uploaded-material-list {
  margin-top: 8px;

  .material-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #67c23a;
    margin-right: 12px;
    margin-bottom: 4px;
    background: #f0f9eb;
    padding: 3px 10px;
    border-radius: 4px;

    i.el-icon-success {
      color: #67c23a;
    }

    .material-name {
      color: #303133;
      max-width: 180px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .remove-icon {
      font-size: 15px;
      color: #f56c6c;
      cursor: pointer;
      transition: color 0.2s;

      &:hover {
        color: #c45656;
      }
    }
  }
}

.uploaded-material {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #67c23a;
  margin-left: 12px;

  i.el-icon-success {
    color: #67c23a;
  }

  .material-name {
    color: #409eff;
    text-decoration: underline;
    cursor: pointer;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .preview-icon {
    color: #909399;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #409eff;
    }
  }

  .remove-icon {
    font-size: 16px;
    color: #f56c6c;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #c45656;
    }
  }
}

.upload-tip {
  display: inline-block;
  margin-left: 12px;
  color: #409eff;
  font-size: 13px;
}

.dialog-footer {
  text-align: right;
  padding: 10px 20px;
  border-top: 1px solid #ebeef5;
}
</style>
